import axios from "axios";
import type { AccesResultDetail } from "../utils/errorUtils";
import { getApiBaseUrl } from "./getApiBaseUrl";
import { tryRefreshAccessToken } from "./refreshAccessToken";

declare module "axios" {
  interface AxiosRequestConfig {
    _retry?: boolean;
    skipPaywall?: boolean;
  }

  interface InternalAxiosRequestConfig {
    _retry?: boolean;
    skipPaywall?: boolean;
  }
}

type PaywallCallback = (detail: AccesResultDetail) => void;
let onPaywallRequired: PaywallCallback | null = null;

function isMonetizationPaywallDetail(
  detail: unknown,
): detail is AccesResultDetail {
  if (!detail || typeof detail !== "object") {
    return false;
  }

  const candidate = detail as Record<string, unknown>;
  return (
    typeof candidate.allowed === "boolean" &&
    typeof candidate.reason === "string" &&
    (
      candidate.requires_subscription === true ||
      candidate.requires_retro === true ||
      candidate.requires_vehicle_payment === true ||
      candidate.reactivare_vehicul === true ||
      candidate.aliniere_abonament_activ === true
    )
  );
}

function shouldOpenPaywallForCurrentRoute(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.location.pathname.startsWith("/foi");
}

function shouldRedirectToLoginForCurrentRoute(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const path = window.location.pathname;
  return (
    path.startsWith("/foi") ||
    path.startsWith("/admin") ||
    path.startsWith("/plata")
  );
}

export function setPaywallCallback(cb: PaywallCallback | null): void {
  onPaywallRequired = cb;
}

/**
 * Centralized axios instance for all API requests.
 * - Uses httpOnly cookies for authentication (withCredentials)
 * - Handles 401 responses by attempting token refresh, then redirecting
 */
const apiClient = axios.create({
  baseURL: "",
  timeout: 30_000,
  withCredentials: true,
  headers: {
    "X-Requested-With": "XMLHttpRequest",
  },
});

apiClient.interceptors.request.use((config) => {
  config.baseURL = getApiBaseUrl();
  return config;
});

// Track whether a refresh is already in progress to avoid parallel attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Response interceptor - handle 401 (unauthorized) responses
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config ?? {};

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/refresh")
    ) {
      originalRequest._retry = true;

      // Coalesce concurrent refresh attempts into one
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = tryRefreshAccessToken().finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
      }

      const refreshed = await refreshPromise;

      if (refreshed) {
        // Retry the original request with the new cookie
        return apiClient(originalRequest);
      }

      // Refresh failed — only force login from protected areas.
      if (
        shouldRedirectToLoginForCurrentRoute() &&
        !window.location.pathname.includes("/login")
      ) {
        window.location.href = "/login?session=expired";
      }
    }

    if (
      error.response?.status === 402 &&
      !originalRequest.skipPaywall &&
      shouldOpenPaywallForCurrentRoute()
    ) {
      const detail = error.response.data?.detail;
      if (isMonetizationPaywallDetail(detail) && onPaywallRequired) {
        onPaywallRequired(detail);
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
